/* eslint-disable @typescript-eslint/no-explicit-any */
import { CanvasEngineEntity, Vector2d } from '@/lib/CanvasEngine'
import type { NodeProps } from '@vue-flow/core'
import { Defect, FieldsUpdatedAt } from './types'
import type { PhotoDrawing } from '@/app/photoDrawing/types'

export interface TaskForUpdate {
  id: number
  name: string
  type: string
  period: string
  year: number
}

export interface Facility {
  id: number | string

  name: string // 시설물명
  name_modified: number // modified_tag
  purpose: string // 용도
  purpose_modified: number // modified_tag
  date: string // 준공일
  date_modified: number // modified_tag
  address: string // 위치
  address_modified: number // modified_tag
  area: number // 면적
  area_modified: number // modified_tag
  top_floor: number // 최상층
  top_floor_modified: number // modified_tag
  bottom_floor: number // 최하층
  bottom_floor_modified: number // modified_tag
  type: string // 종류
  type_modified: number // modified_tag
  memo: string // 메모
  memo_modified: number // modified_tag
  modified: number
  deleted: number
  completionDate: string
  totalArea: number
  task_id?: number
}

export interface FacilityBranch extends Facility {
  facility_id: number // 시설물 원본 id
  task_id: number // 과업 id
  tag: string // 브랜치 태그
  tag_modified: number // modified_tag
  created_at: number // timestamp
  floorplans: Floorplan[]
}

export interface FacilityBranchPhoto {
  id: number | string
  facility_branch_id: number
  location: string
  location_modified: number // modified_tag
  url: string
  deleted: number
  url_modified: number // modified_tag
}

export type FileType = 'pdf' | 'webp' | 'png' | 'jpeg' | 'dwg'

// facility_branch_id가 null이면 최신 도면
// facility_branch_id가 있으면 브랜치된 도면
export interface Floorplan {
  id: number | string
  facility_id: number
  facility_branch_id?: number // null 이면 facility와 직접연결된 최신도면
  origin_floorplan_id?: number
  is_cad?: boolean
  name: string // 도면명
  name_modified: number // modified_tag
  location: string // 도면 층수
  location_modified: number // modified_tag
  structure_material: string // 구조 재료
  structure_material_modified: number // modified_tag
  structure_type: string // 구조 형식
  structure_type_modified: number // modified_tag
  file_type: FileType // 보여줄 도면 파일 형식 (pdf, png, jpeg 등)
  file_type_modified: number // modified_tag
  file_url: string // 보여줄 도면 URL
  file_url_modified: number // modified_tag
  origin_file_type: FileType // 원본 도면 TYPE
  origin_file_type_modified: number // modified_tag
  origin_file_url: string // 원본 도면 URL
  origin_file_url_modified: number // modified_tag
  rotate: number // 도면 회전 각도, default 0
  rotate_modified: number // modified_tag
  x_min_offset: number // 도면 x축 최소 offset (이하 안쓰이는 것으로 추정)
  y_min_offset: number // 도면 y축 최소 offset
  x_max_offset: number // 도면 x축 최대 offset
  y_max_offset: number // 도면 y축 최대 offset
  offset_modified: number // modified_tag
  deleted: number
  // 타일 관련 속성
  // 타일 파일명은 {floorplan_id}_{sclae (100단위)}_{col}_{row}_{tile_config.createdAt}.png 형식
  is_tiled?: boolean // 타일링 처리 여부
  tile_config?: {
    bounds: { width: number; height: number }
    levels: {
      scale: number
      width: number
      height: number
      cols: number
      rows: number
    }[]
    tileSize: number
    floorplanId: number // 층 id
    createdAt: number // 생성일시 (unixtimestamp)
  } // 타일 설정 정보 (JSON)
  tile_base_url?: string // 타일 저장 경로 prefix
}

export interface FloorplanMetadata {
  id: number | string
  floorplan_id: number
  balloon_scale: number
  balloon_scale_modified: number
  point_shape_scale: number
  point_shape_scale_modified: number
  connected_line_thickness_scale: number
  connected_line_thickness_scale_modified: number
  area_shape_border_thickness_scale: number
  area_shape_border_thickness_scale_modified: number
  balloon_font_scale: number
  balloon_font_scale_modified: number
  width: number
  height: number
  deleted: number
  balloon_font_color?: string
  balloon_fill_color?: string
  balloon_border_color?: string
}

/**
 * 구조물 결함 데이터의 속성을 정의하는 인터페이스
 */
export interface ShapeDataProperty {
  /**
   * 구조물의 부재 정보
   * 결함이 발생한 구조물의 부재 요소(예: 기둥, 보, 슬래브 등)를 나타냅니다.
   */
  structural_component: string
  /**
   * 부재 정보의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 부재 정보가 원본에서 수정되었는지 여부를 추적합니다.
   */
  structural_component_modified: number

  /**
   * 결함의 유형 분류
   * 결함의 종류(예: 균열, 박리, 박락, 누수 등)를 분류합니다.
   */
  classification: string
  /**
   * 결함 유형 분류의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 결함 유형 분류가 원본에서 수정되었는지 여부를 추적합니다.
   */
  classification_modified: number

  /**
   * 결함의 진행 상태
   * 0: 진행 안됨, 1: 진행중
   * 결함이 현재 진행 중인지 또는 안정화되었는지를 나타냅니다.
   */
  progressing: number
  /**
   * 결함 진행 상태의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 결함 진행 상태가 원본에서 수정되었는지 여부를 추적합니다.
   */
  progressing_modified: number

  /**
   * 결함의 너비 값
   * 결함의 너비를 측정한 값으로, 단위는 일반적으로 mm입니다.
   */
  width: string
  /**
   * 너비 값의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 너비 값이 원본에서 수정되었는지 여부를 추적합니다.
   */
  width_modified: number

  /**
   * 결함의 길이 값
   * 결함의 길이를 측정한 값으로, 단위는 일반적으로 mm입니다.
   */
  length: string
  /**
   * 길이 값의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 길이 값이 원본에서 수정되었는지 여부를 추적합니다.
   */
  length_modified: number

  /**
   * 결함의 면적 값
   * 결함이 차지하는 면적으로, 단위는 일반적으로 m²입니다.
   */
  area: string
  /**
   * 면적 값의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 면적 값이 원본에서 수정되었는지 여부를 추적합니다.
   */
  area_modified: number

  /**
   * 결함의 개수
   * 동일한 유형의 결함이 발견된 개수를 나타냅니다.
   */
  count: string
  /**
   * 개수 값의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 개수 값이 원본에서 수정되었는지 여부를 추적합니다.
   */
  count_modified: number

  /**
   * 구조체 여부
   * 0: 비구조체, 1: 구조체
   * 결함이 구조적으로 중요한 부분에 있는지 여부를 나타냅니다.
   */
  is_structural: number
  /**
   * 구조체 여부의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 구조체 여부가 원본에서 수정되었는지 여부를 추적합니다.
   */
  is_structural_modified: number

  /**
   * 결함에 대한 보수 방안
   * 결함을 해결하기 위한 권장 보수 방법을 설명합니다.
   */
  repair_plan: string
  /**
   * 보수 방안의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 보수 방안이 원본에서 수정되었는지 여부를 추적합니다.
   */
  repair_plan_modified: number

  /**
   * 결함 발생 원인 추정 정보
   * 결함이 발생한 원인에 대한 추정 내용을 설명합니다.
   */
  cause_estimate: string
  /**
   * 발생 원인 추정의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 발생 원인 추정이 원본에서 수정되었는지 여부를 추적합니다.
   */
  cause_estimate_modified: number

  /**
   * 사용된 템플릿 정보
   * 결함 데이터를 기록할 때 사용된 템플릿의 식별자 또는 이름입니다.
   */
  template: ShapeTemplateType
  /**
   * 템플릿 정보의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 템플릿 정보가 원본에서 수정되었는지 여부를 추적합니다.
   */
  template_modified: number

  /**
   * 도형 스타일의 수정 여부 플래그
   * 0: 미수정, 1: 수정됨
   * 도형 스타일이 원본에서 수정되었는지 여부를 추적합니다.
   */
  shapeType_modified?: number

  admin_index?: string
  admin_index_modified?: number

  // 전차 전환 시 현재차 스타일 백업 (전차→현차 복원용)
  savedCurrentStyles?: {
    color: string
    shapeDataColor: string
    balloonFontColor: string
    balloonFillColor: string
  }
}
export type ShapeTemplateType =
  | ''
  | 'custom'
  | 'custom-RC'
  | 'custom-SS'
  | 'custom-SRC'
  | 'canvas-brush'
  // 하자진단: template = 구분(structure), cause_estimate = 점검시기(inspection_period)
  | 'defect-COMMON'
  | 'defect-EXCLUSIVE'

export interface OriginShapeData {
  color: string
  balloonScale: number
  pointShapeScale: number
  balloonFillColor: string
  balloonFontColor: string
  balloonFontScale: number
  areaBorderThicknessScale: number
  connectedLineThicknessScale: number
  layerId: string
  layerName: string
}

// 부록에서 사용하기 위한 Shape
export interface ShapeForDocs extends Defect {
  exportIndex: string // 결함번호
  photoIndex?: number // 사진번호 (외관조사 사진첩 기준 정본 번호라 출력물마다 같음, buildPhotoIndexMap 참고)
  imageUrl: string // 대표사진의 URL
  prevImageUrl?: string // 이전 사진의 URL
  isPrev?: boolean // 이전회차 여부 (이전이면서 현회차 사진이 없어야함)
  floorName: string // 층 이름
  parent_defect_id: number | string | null
}

// 부록 - 결함사진 zip 용 타입
export interface DefectPhotoZipContent {
  path: string
  url: string
  fileName: string
  drawing?: PhotoDrawing | null // 그리기 포함 출력이면 담기 전에 로컬 합성 (검증된 값)
}
export interface DefectPhotoZip {
  name: string
  content: DefectPhotoZipContent[]
}
// DefectPhoto interface
export interface DefectPhoto {
  id: number | string
  shape_id: number | string
  is_previous: number

  url: string // 결함 사진 URL
  url_modified: number // modified_tag
  is_main: boolean // 대표사진 여부
  is_main_modified: number // modified_tag

  deleted: number
  drawing?: unknown // 사진 위 그리기 (검증 전 원본, 공유 페이지 등)
}

export type DefectPhotoType = 'new' | 'prev' | null
/**
 * 슬라이딩 팝업 모달의 공통 모드 enum
 * Full: 전체 화면 모드
 * Minimize: 최소화 모드
 * Maximize: 최대화 모드 (전체화면, 테두리 없음)
 * None: 닫힌 상태
 */
export enum SlidingPopupMode {
  Full = 'full',
  Minimize = 'minimize',
  Maximize = 'maximize',
  None = 'none',
}

export enum UserDefectInputStructureEnum {
  RC = 'RC',
  SS = 'SS',
  SRC = 'SRC',
  COMMON = 'COMMON', // 하자진단 - 공용
  EXCLUSIVE = 'EXCLUSIVE', // 하자진단 - 전유
}

export enum UserDefectInputTypeEnum {
  STRUCTURE = 'STRUCTURE',
  MEMBER = 'MEMBER',
  DEFECT = 'DEFECT',
  CAUSE = 'CAUSE',
  REMEDY = 'REMEDY',
  MEMO = 'MEMO',
  FACILITY_IMG_DISCRIPTION = 'FACILITY_IMG_DISCRIPTION',
}

export type InspectionPeriod = 'PRE' | 'POST'

export interface UserDefectInputItem {
  id: number
  company: string // 계정 id
  building_type: string | null // 건물 종류 (하자진단 전용: 아파트/오피스텔 등, 안전진단: null)
  structure: UserDefectInputStructureEnum | null // 구조/구분 (안전진단: RC/SS/SRC, 하자진단: COMMON/EXCLUSIVE)
  inspection_period: InspectionPeriod | null // 점검 시기 (하자진단 전용: PRE=사용검사전, POST=사용검사후)
  type: UserDefectInputTypeEnum // 부재, 결함, 발생원인, 보수방안, 메모
  member: string | null // 부모 부재
  defect: string | null // 부모 결함
  values: UserDefectInputItemValue[]
  values_modified: number // modified_tag
}
export interface UserDefectInputItemValue {
  name: string
  is_favorite?: 0 | 1 // 즐겨찾기 여부
  value_type?: 'WIDTH_LENGTH' | 'AREA' // 값 타입 (폭x길이, 면적)
  is_default?: 0 | 1 // 기본값 여부
  parent_id?: number // 부모의 id (프론트에서만 사용)
}

// 하자진단 하자항목 정의 (부록 문구). 위치 배치·순서는 UserDefectInputItem.values가 정본, 연결 키 = 구분·시기·이름
export interface DefectItemDefinition {
  id: number
  company_id: number
  building_type: string
  structure: 'COMMON' | 'EXCLUSIVE'
  inspection_period: InspectionPeriod
  name: string // 하자항목명 (= 결함 classification, 전유 POST는 공종명)
  include_by_default: boolean // 기본 항목 (과업 생성 시 선체크)
  problem: string | null // 문제점 문구 (POST 전용)
  cost: string // 보수비 산정방법
  checks: (0 | 1)[] // 하자발생내용 체크 (PRE·공용 POST 9칸, 전유 POST 13칸)
  etc_note: string // 하자발생내용 기타 특이사항
  ref_ids: number[] // 관련근거 id (순서 = 설계도서 줄 순서, 최대 3)
  fields_updated_at: Record<string, string>
  updated_at: string
}

// 하자진단 관련근거 (여러 하자항목이 재사용, 수정하면 쓰는 항목 전부에 반영)
export interface DefectItemRef {
  id: number
  company_id: number
  text: string
  image_url: string | null // storage://homecheck-service/... (null: 텍스트 전용)
  image_width: number | null
  image_height: number | null
  usage_count: number // 이 근거를 쓰는 하자항목 수
  updated_at: string
}

// 항목편집 엑셀 가져오기·전체 삭제 결과 (서버 company-defect-items/import·clear)
export interface DefectItemImportStats {
  refs_created: number
  defs_created: number
  defs_updated: number
  defs_deleted: number
  rows_written: number
  rows_deleted: number
}

export type DefectItemKey = Pick<
  DefectItemDefinition,
  'building_type' | 'structure' | 'inspection_period'
>
export type DefectItemEditable = Partial<
  Pick<
    DefectItemDefinition,
    'include_by_default' | 'problem' | 'cost' | 'checks' | 'etc_note' | 'ref_ids'
  >
>

export interface DeviceEventLog {
  id: number | string
  task_id: number
  related_id: number | string
  event_type: string
  environment: string
  company_id: number
  created_user_id: number
  author: string
  old_value: string
  new_value: string
  fields_updated_at: {
    [key: string]: number
  }
  created_at: string
  updated_at: string
  deleted_at: string
  deleted?: number
  modifiedFromLocal?: boolean
  localRelatedId?: string
}

export type ChangedField = {
  key: string
  before: any
  after: any
}
export type ChangedItem = {
  id: string | number
  isNew: boolean
  changedFields: ChangedField[]
}

export type LogItem = {
  type: string
  related_id: string | number
  new_value: string
  old_value: string
}

//flow 관련 타입

export interface CustomNodeData {
  label: string
  content?: string
  color?: string
  isFocused?: boolean
}
export interface CustomNodeProps extends NodeProps {
  data: CustomNodeData
}

// 디자인 리뉴얼로 추가됨

export interface TaskItem {
  convertedName: string
  company: string
  company_code: string
  deleted: number
  end_date: string
  end_date_modified: number
  hidden: number
  hidden_modified: number
  id: number
  memo: string
  memo_modified: number
  modified: number
  name: string
  name_modified: number
  num: number
  start_date: string
  start_date_modified: number
  type: string
  facilities: TaskFacility[]
  period: string
  year: number
  portfolio_id?: number
  previous_task_id?: number
  modified_at?: number
  attribute?: any
  facility_branchs?: FacilityBranch[]
}

export interface TaskFacility {
  facility_id: number
  id: number
  facility_name: string
  shapeIds: number
  photoIds: number
  task_id: number
  floorplans: Floorplan[]
  fileId?: number
  deleted: number
}

// 앱 버전 관리
export interface AppVersion {
  id: number
  version: string // YY.M.순번
  memo: string
  url: string // gcs 경로
  created_at: string
  force_update: 0 | 1 // 강제 업데이트 여부
  build_type: 'dev' | 'production' // 빌드 타입
}

export interface TaskDataLength {
  id: number
  facility_photos: number
  floorplans: number
  defects: number
  defect_photos: number
}

// 유저 설정
export interface UserSettingConfig {
  key: string
  title: string
  settings: UserSettingConfigSettings[]
}
export interface UserSettingConfigSettings {
  key: string
  name: string
  type: 'radio' | 'checkbox' | 'text'
  subtype: 'slider'
  values: { key?: string; name: string; value?: number | string | boolean }[]
  default: number | string | boolean | (number | string | boolean)[]
  value: number | string | boolean | (number | string | boolean)[]
}

// 출력 종류명
export type ExportTemplateType =
  | 'FACILITY_GALLERY' // 시설물사진첩
  | 'STATUS_OUTSIDE_GALLERY' // 외관조사사진첩
  | 'DAMAGE_DEFECT' // 손상결함표
  | 'TASK_COMPARISON_GALLERY' // 전차비교사진첩
  | 'INSPECTION_LOCATION' // 조사위치도
  | 'DEFECT_PHOTO' // 결함사진
  | 'DEFECT_DETAIL_INSPECTION' // 정밀안전점검 내용
  | 'CUSTOM' // 기타(주로 pyhwpx 사용)
  | 'SUPPLEMENT' // 부록
  | 'DOC' // 문서
  | 'DEFECT_POST' // 하자진단 사용검사후
  | 'DEFECT_POST_PDF' // 하자진단 사용검사후 (PDF)
  | 'DEFECT_PRE' // 하자진단 사용검사전
  | 'DEFECT_PRE_PDF' // 하자진단 사용검사전 (PDF)
  | 'DEFECT_LIST_EXCEL' // 하자진단 하자목록표(엑셀)

// 출력 탬플릿 옵션
export type ExportTemplateOptions = {
  includeTextOption: boolean // 이미지에 텍스트 포함 선택 여부
  includeBrushOption: boolean // 자유그리기 포함 선택 여부
  includeCompletedPrev: boolean // 이전 회차 완료 여부 포함 선택 여부
  widthLengthUnitOption: boolean // 폭, 길이 단위 선택 여부
  allFloorNumberingOption: boolean // 모든 층 번호 매기기 선택 여부
  includeBalloonBackgroundOption: boolean // 벌룬 배경 포함 선택 여부
  includeInspectionTypeOption: boolean // 외관조사 / 정밀점검 선택 여부
  includeDefectStructureOption: boolean // 구분 선택 (공용/전유)
  includeDrawingOption?: boolean // 사진 그리기 포함 선택 여부 (사진이 들어가는 양식, plan/photo-brush/06)
}

// 출력 탬플릿
export type ExportTemplate = {
  id: number
  type: ExportTemplateType
  template_key: string | null // 템플릿 키 (주로 type = CUSTOM 일때 사용, Null 허용)
  url: string // gcs 경로
  ext: 'hwpx' | 'hwp' | 'xlsx' | 'jpg' | 'dwg' | 'zip'
  name: string
  is_custom: boolean // 사용자 정의 템플릿 여부 (false면 젠트릭스 기본양식)
  created_at: string // 생성일시
  options: ExportTemplateOptions // 출력 옵션
}

export interface QuickInputData {
  template: ShapeTemplateType
  structural_component: string
  is_structural: number
  classification: string
  progressing: number
  width: string
  length: string
  count: string
}

export interface QuickInputItem extends QuickInputData {
  id: string
  task_id: number
  created_at: string
}

export interface QuickInputOtherItem {
  width: string[]
  length: string[]
}

// 입력항목 프리셋
export interface UserDefectInputPreset {
  id: number
  name: string // 프리셋명
  template: ShapeTemplateType // 구조 (custom-RC 형태)
  structural_component: string // 부재
  is_structural: number // 구조체 여부
  classification: string // 결함 유형
  progressing: number // 진행 여부
  width: string // 폭
  length: string // 길이
  count: string // 개수
  repair_plan: string // 보수 방안
  cause_estimate: string // 발생 원인 추정
  created_at: string
  updated_at: string
  fields_updated_at?: Record<string, string>
}
export interface TreeViewData {
  id: string
  label?: string
  checked?: boolean
  noCheck?: boolean // 체크박스 비활성화 여부
  partialCheck?: boolean // 부분 체크 상태
  noSlot?: boolean // 슬롯 사용 여부
  children?: TreeViewData[] | null
  isOpen?: boolean
  tag?: 'shape' | 'floor' | 'facility'
  progressing?: number
  comments?: ShapeComment[]
  pages?: DocItem[]
  template_id?: number
}

export interface TreeViewShape extends TreeViewData {
  indexing: number
  color: string
  structural_component: string
  classification: string
  progressing: number
  width: string
  length: string
  count: string
  memo: string
  parent_defect_id: number | string
  selected_tags: string[]
  floorplan_id: string
  sort_key: string
  comments?: ShapeComment[]
}

export interface ShapeComment {
  id: number
  defect_id: number
  task_id: number
  facility_id: number
  floorplan_id: number
  company_id: number
  created_user_id: number
  content: string
  fields_updated_at: Record<string, string>
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface ShapeCommentPhoto {
  id: number
  defect_comment_id: number
  defect_id: number
  task_id: number
  facility_id: number
  floorplan_id: number
  company_id: number
  created_user_id: number
  url: string
  sort_key: string
  fields_updated_at: Record<string, string>
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface UserSettingConfigConv {
  userSetBalloonScale: number
  userSetConnectedLineThicknessScale: number
  userSetBalloonFontScale: number
  userSetPointShapeScale: number
  userSetAreaBorderThicknessScale: number
  userSetBalloonStyleBordeColor: string
  userSetBalloonStyleFillColor: string
  userSetBalloonStyleFontColor: string
  userSetBalloonFillOpacity: number
}

export interface TripodShapePoints {
  mainAxisPoint1: number[]
  mainAxisPoint2: number[]
  dots: number[][]
  // direction: 'vertical' | 'horizontal'
  mainShapeId: string
  targetIds: string[]
  tailStyle: 'arrow' | 'dot'
}

export interface BalloonBoundingRect {
  start: Vector2d
  end: Vector2d
  center: Vector2d // 실제 balloon 중심점 (정밀도 보장)
  entity: CanvasEngineEntity
  needPos: {
    type: 'left' | 'right' | 'top' | 'bottom'
    x: number
    y: number
  }
  distance: number
}

export interface DocEditPageObj {
  id: string
  type:
    | 'text'
    | 'file'
    | 'checkbox'
    | 'radio'
    | 'table'
    | 'select'
    | 'toogle'
    | 'number'
    | 'date'
    | 'table'
    | 'radio-v'
    | 'multi_file'
    | 'shape_table'
    | 'ox'
  width: number
  height: number
  x: number
  y: number
  defaultValue?: any
  value?: string
  //라디오버튼에서 쓰임
  noLabel?: boolean
  //테이블, 체크박스, 라디오, ox 버튼 등
  items?: any
  //날짜
  isRange?: boolean
  //다른 페이지나 정보와 연동된 인풋인지여부
  canLink?: boolean
  page_no?: number
}

export interface DocEditPageObjTableItem {
  maxRowLength: number
  columns: string[]
  rows: DocEditPageObj[][] //DocEditPageObj 2차원 배열
}

export interface BrushData {
  id: string
  lines: {
    points: number[]
    strokeWidth: number
    stroke: string
    createdTime?: number
  }[]
  transform: {
    x: number
    y: number
    scaleX: number
    scaleY: number
    rotation: number
  }
}

export interface UserExportReportRecord {
  id: number | string
  template_id: number
  company_id: number
  task_id: number
  facility_id: number
  page_no: number
  items: DocEditPageObj[] | string // JSON
  fields_updated_at: FieldsUpdatedAt
  created_at: string
  updated_at: string
  deleted_at: string | null
  brush_data: BrushData[] | null
  localStringId?: string
  modifiedFromLocal?: boolean
  url?: string //로컬 오프라인에서 사용하기 위해 최초에 저장해놓음
}

export interface AddUserExportReportRecord {
  facility_id?: number
  template_id?: number
  task_id?: number
  page_no?: number
  items?: DocEditPageObj[] | string // JSON
  brush_data?: BrushData[] | null
}

export interface TreeViewDoc extends TreeViewData {
  task_id: number
  facility_id: number
  template_id: number
  template_key: number
  inputs?: DocEditPageObj[]
  pages: any[]
  docTitle?: string
}

export interface DocItem {
  docTitle: string
  template_id: number
  template_key: string
  template_type: string
  task_id: number
  facility_id: number
  pages: UserExportReportRecord[]
}
export interface FflateLib {
  unzip: (
    data: Uint8Array,
    callback: (err: Error | null, files: Record<string, Uint8Array>) => void,
  ) => void
  zip: (
    files: Record<string, Uint8Array>,
    callback: (err: Error | null, data: Uint8Array) => void,
  ) => void
}

// 빌드 모듈 타입 정의
export interface ScriptModule {
  mergeOrGenerateData: (...args: unknown[]) => Promise<DocItem>
}
export interface ShapeForPhotos {
  prevPhotoUrl: string
  newPhotoUrl: string
  prevTaskType: string
  newTaskType: string
  floorName: string
  floorplanId: number | string
  structureComponent: string
  classification: string
  progressing: number
  width: string
  length: string
  area: string
  color: string
  indexing: number | string
  localStringId: string
}

export interface UserExportReport {
  id: number
  name: string
  template_ids: number[]
  url: string
  company: string
  ext: string
  created_at: number
  deleted: number
}

export enum CompanyType {
  BIZ = 'BIZ',
  IND = 'IND',
}

export type CompanyCategory = 'SAFETY' | 'DEFECT' | 'QUALITY'

// 사용자 역할 (MANAGER: 품질점검 총괄, MEMBER: 일반)
export type UserRole = 'MANAGER' | 'MEMBER'

export interface CompanyInfo {
  id: number // PK
  name: string // 이름
  category: CompanyCategory // 서비스 분류 (SAFETY: 안전진단, DEFECT: 하자진단, QUALITY: 품질점검)
  fields_updated_at: Record<string, string> // 컬럼별 마지막 수정 시간
  created_at: Date // 생성일시
  updated_at: Date // 수정일시
  deleted_at: Date | null // 삭제일시
  type: CompanyType
}

// 엑셀 컬럼 AI 매핑 결과 (POST ai/excel-column-mapping)
export interface ExcelColumnMappingResult {
  // headerCandidates 내 헤더 행 인덱스 (0부터)
  headerRowOffset: number
  columns: Array<{
    // 엑셀 컬럼 번호 (1부터)
    columnIndex: number
    // 매핑된 표준 필드 (매핑 불가/결합 컬럼 시 null)
    field: string | null
    // 원본 단위 (폭/길이/면적 컬럼만, 추정 불가 시 null)
    unit: string | null
    // 결합 컬럼일 때 분해할 필드 순서 (예: ['width','length'])
    compound?: string[] | null
    // compound 각 항목의 원본 단위 (compound와 같은 순서, 모르면 null)
    compoundUnits?: Array<string | null> | null
  }>
}

export interface UserInfo {
  id: number // PK
  login_id: string // 로그인 ID
  company_id: number | null // 회사정보 id
  email: string | null // 이메일 주소
  name: string // 이름
  role?: UserRole // 역할 (구버전 서버 응답 호환을 위해 옵셔널)
  company: CompanyInfo // 회사 정보
  fields_updated_at: Record<string, string> // 컬럼별 마지막 수정 시간
  created_at: Date // 생성일시
  updated_at: Date // 수정일시
  deleted_at: Date | null // 삭제일시
  isAdmin?: boolean // 관리자 권한 (클라이언트에서만 사용)
}

// ──────────────────────────────────────────────
// 보고서 PDF → 전차 결함 등록 (report import)
// 서버 타입(zentrix-api/src/types/db.ts)과 동기화
// ──────────────────────────────────────────────

export type ReportImportStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'REVIEW'
  | 'COMMITTING'
  | 'COMMITTED'
  | 'FAILED'
  | 'CANCELED'

export interface ReportImportProgress {
  stage?: string // DOWNLOAD/LAYOUT/TABLE/PHOTO/NORMALIZE/MATCH/UPLOAD/SAVE
  percent?: number // 0~100
  message?: string // 표시 문구
  updated_at?: string // 갱신 일시 (ISO)
}

export interface ReportImportSectionMapping {
  key: string // 섹션 정규화 키 (items.section_key 와 대응)
  label: string // 섹션 원문
  facility_hint: string // 섹션이 속한 시설물 힌트 (변형 B 구역명 등)
  item_count: number // 섹션 항목 수
  proposed: {
    facility_id: number | null
    floorplan_id: number | null
    confidence: number
    reason: string
  } | null // 워커 제안
  confirmed: { facility_id: number; floorplan_id: number } | null // 사용자 확정
  skip: boolean // 섹션 전체 제외
}

export interface ReportImportCommitResult {
  created_defects: number
  created_photos: number
  skipped_items: number
  floorplan_counts: { floorplan_id: number; count: number }[]
  warnings: string[]
}

// 워커가 추출한 보고서 '결함조사 위치도' (층별 1장 이상)
export interface ReportImportLocationMap {
  page_no: number // 보고서 페이지
  label: string // 위치도 제목에서 뗀 층 라벨 (예: 지상 1층)
  section_key: string // 섹션 키 (section_mappings.key 와 같은 규칙)
  section_keys?: string[] // 이 위치도가 해당하는 섹션 키들 (정확 일치 + 층 핵심 키 일치)
  url: string // storage://… webp
}

export interface ReportImportSummary {
  page_count?: number
  table_row_count?: number
  photo_count?: number
  linked_count?: number
  unlinked_count?: number
  warnings?: string[]
  item_count?: number // 적재한 항목 수 (TABLE + PHOTO_ONLY)
  facility_name_hint?: string // 표지에서 추정한 시설물명
  uploaded_photos?: number // GCS 에 올린 크롭 사진 수
  location_maps?: ReportImportLocationMap[] // 보고서 위치도 (없으면 빈 배열/미정의)
  upload_failed?: number // 업로드 실패 사진 수
  extraction?: Record<string, unknown> // 워커 추출 요약
  elapsed_ms?: number // 워커 처리 소요 (ms)
  attempts?: number
  user_message?: string // 실패 시 사용자 안내 문구
  error_stage?: string // 실패한 단계
  commit?: ReportImportCommitResult
}

export interface ReportImport {
  id: number
  company_id: number
  task_id: number
  created_user_id: number
  status: ReportImportStatus
  source_file_url: string // storage://… /source.pdf
  source_file_name: string
  source_file_size: number
  gcs_root: string // zentrix | zentrix-dev
  vendor_hint: string // 표지에서 추정한 작성 회사명
  template_variant: string // A | B | UNKNOWN
  progress: ReportImportProgress
  section_mappings: ReportImportSectionMapping[]
  summary: ReportImportSummary
  error_message: string
  attempts: number
  worker_id: string
  heartbeat_at: string | null
  started_at: string | null
  finished_at: string | null
  committed_at: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface ReportImportItem {
  id: number
  report_import_id: number
  company_id: number
  task_id: number
  seq: number // 보고서 내 순서
  page_no: number
  section_key: string
  section_label: string
  location_raw: string // 위치 원문 (변형 B)
  report_no: string // 보고서 표의 NO
  photo_no_raw: string
  photo_no_key: string
  member_raw: string
  structural_type_raw: string
  defect_raw: string
  width_raw: string
  length_raw: string
  area_raw: string
  count_raw: string
  progressing_raw: string
  leaking_raw: string
  cause_raw: string
  remarks_raw: string
  structural_component: string // 부재 (정규화)
  classification: string // 결함 유형 (정규화)
  width: string // mm
  length: string // mm
  area: string // ㎡
  count: string
  cause_estimate: string
  repair_plan: string
  is_structural: boolean
  progressing: number // 0: 진행없음, 1: 진행중
  memo: string
  normalize_confidence: number // 0~1
  normalize_notes: string
  photo_url: string | null // storage://… webp (NULL 이면 사진 없음)
  photo_caption: string
  photo_page_no: number
  photo_floor_text: string
  source: 'TABLE' | 'PHOTO_ONLY'
  is_excluded: boolean
  defect_id: number | null // 커밋으로 생성된 결함 ID
}

export interface ReportImportFacilityTree {
  id: number
  name: string
  floorplans: { id: number; name: string }[]
}

export interface ReportImportDetail extends ReportImport {
  items: ReportImportItem[]
  facilities: ReportImportFacilityTree[]
}
